/**
 * The unified list model behind the Customize page.
 *
 * Browse and Installed distinguish catalog connectors from personal MCP
 * definitions saved by the user. A saved definition does not indicate that
 * a server is reachable or active in a session.
 *
 * Everything the row needs is projected onto this union up front. Rows do not
 * reach back into the wire models.
 */

import {
  type McpSecretReference,
  type McpServerConfig,
  type McpServerMap,
  type McpTransport,
  collectSecretReferences,
  isRemoteTransport,
} from './mcp'
import {type NormalizedStatus, type Plugin, normalizeConnectionStatus, pluginDisplayName} from './connectors'

export type CustomizationSource = 'connector' | 'mcp'

interface CustomizationBase {
  /** Unique within its source. Not unique across sources; use `customizationKey`. */
  id: string
  name: string
  description?: string
}

export interface ConnectorCustomization extends CustomizationBase {
  source: 'connector'
  logo?: string
  status: NormalizedStatus
  /** Retained so the detail dialog can read connector-only fields. */
  plugin: Plugin
}

export interface McpCustomization extends CustomizationBase {
  source: 'mcp'
  transport: McpTransport
  config: McpServerConfig
  /** Reference names found in the definition, without checking availability. */
  secrets: McpSecretReference[]
}

export type Customization = ConnectorCustomization | McpCustomization

/**
 * List key. A connector and an MCP server may share a name, so neither `id`
 * alone nor `name` alone is safe as a React key.
 */
export function customizationKey(item: Customization): string {
  return `${item.source}:${item.id}`
}

export function toConnectorCustomization(plugin: Plugin): ConnectorCustomization {
  return {
    source: 'connector',
    id: plugin.name,
    name: pluginDisplayName(plugin),
    description: plugin.description,
    logo: plugin.logo,
    status: normalizeConnectionStatus(plugin.connection?.status),
    plugin,
  }
}

/**
 * Projects one entry of the `mcpServers` map.
 *
 * The summary line is derived from fields understood by the form. A remote
 * server shows its host, which is the useful part of a long URL. A process
 * server shows its command.
 */
export function toMcpCustomization(name: string, config: McpServerConfig): McpCustomization {
  return {
    source: 'mcp',
    id: name,
    name,
    description: describeMcpServer(config),
    transport: config.type,
    config,
    secrets: collectSecretReferences(config),
  }
}

export function toMcpCustomizations(servers: McpServerMap | undefined): McpCustomization[] {
  if (!servers) return []
  return Object.entries(servers).map(([name, config]) => toMcpCustomization(name, config))
}

function describeMcpServer(config: McpServerConfig): string | undefined {
  if (isRemoteTransport(config.type)) {
    if (!config.url) return undefined
    try {
      // No base URL on purpose. Resolving against the current origin would show
      // github.com as the host for a malformed entry, which misleads the reader.
      // eslint-disable-next-line no-restricted-syntax
      return new URL(config.url).host
    } catch {
      return config.url
    }
  }

  if (!config.command) return undefined
  return [config.command, ...(config.args ?? [])].join(' ')
}

const TRANSPORT_LABELS: Record<McpTransport, string> = {
  http: 'HTTP',
  sse: 'SSE',
  local: 'Local',
  stdio: 'Local',
}

/** Short badge text naming where the row came from. */
export function customizationTypeLabel(item: Customization): string {
  return item.source === 'connector' ? 'Connector' : TRANSPORT_LABELS[item.transport]
}

/**
 * Whether the row belongs on the Installed tab.
 *
 * A connector is installed once its OAuth flow has completed. A personal MCP
 * server is installed as soon as it exists, since defining one is the act of
 * adding it. Runtime readiness is outside this view.
 */
export function isInstalled(item: Customization): boolean {
  return item.source === 'connector' ? item.status === 'connected' : true
}

/** Case-insensitive match over the text a row actually shows. */
export function filterCustomizations(items: Customization[], query: string): Customization[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return items

  return items.filter(item => {
    const haystack = [item.name, item.id, item.description ?? '', customizationTypeLabel(item)]
    return haystack.some(value => value.toLowerCase().includes(needle))
  })
}

/**
 * Stable display order: connectors first, then personal servers, each sorted by
 * name. Keeps the curated catalog recognizable as the user's own list grows.
 */
export function compareCustomizations(a: Customization, b: Customization): number {
  if (a.source !== b.source) return a.source === 'connector' ? -1 : 1
  return a.name.localeCompare(b.name)
}
